import { AxiosResponse } from 'axios';
import { CheckinSummary } from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type UnreviewedCheckinsByTeams = {
  checkins: CheckinSummary[];
  name: string;
  usersToNotify: number;
  teamId: string;
};

export type PendingUsersCheckins = {
  totalOfPendingCheckins: number;
};

export type UnreviewedCheckinsByTeamsResponse = {
  teams: UnreviewedCheckinsByTeams[];
};

const getAllCheckinsUnreviewed = async (): Promise<AxiosResponse<CheckinSummary[]>> => {
  const { data, error } = await supabase
    .from('checkins')
    .select('*')
    .eq('is_reviewed', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse(data as unknown as CheckinSummary[]);
};

const getAllCheckinsUnreviewedByTeamId = async (
  teamId: string
): Promise<AxiosResponse<UnreviewedCheckinsByTeamsResponse>> => {
  // Get team members, then their unreviewed checkins
  const { data: members, error: membersError } = await supabase
    .from('team_members')
    .select('profile_id')
    .eq('team_id', teamId);

  if (membersError) throw membersError;

  const profileIds = (members || []).map((m: any) => m.profile_id);

  if (profileIds.length === 0) {
    return asResponse<UnreviewedCheckinsByTeamsResponse>({ teams: [] });
  }

  const { data: teamData, error: teamError } = await supabase
    .from('teams')
    .select('id, name')
    .eq('id', teamId)
    .single();

  if (teamError) throw teamError;

  const { data: checkins, error: checkinsError } = await supabase
    .from('checkins')
    .select('*')
    .eq('is_reviewed', false)
    .in('profile_id', profileIds)
    .order('created_at', { ascending: false });

  if (checkinsError) throw checkinsError;

  return asResponse<UnreviewedCheckinsByTeamsResponse>({
    teams: [
      {
        teamId: teamData.id,
        name: teamData.name,
        checkins: (checkins || []) as unknown as CheckinSummary[],
        usersToNotify: (checkins || []).length,
      },
    ],
  });
};

const getAllCheckinsUnreviewedByTeam = async (): Promise<
  AxiosResponse<UnreviewedCheckinsByTeamsResponse>
> => {
  // Get all teams with their members' unreviewed checkins
  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('id, name, team_members(profile_id)');

  if (teamsError) throw teamsError;

  const result: UnreviewedCheckinsByTeams[] = [];

  for (const team of teams || []) {
    const profileIds = (team.team_members || []).map((m: any) => m.profile_id);
    if (profileIds.length === 0) continue;

    const { data: checkins, error: checkinsError } = await supabase
      .from('checkins')
      .select('*')
      .eq('is_reviewed', false)
      .in('profile_id', profileIds)
      .order('created_at', { ascending: false });

    if (checkinsError) throw checkinsError;

    if ((checkins || []).length > 0) {
      result.push({
        teamId: team.id,
        name: team.name,
        checkins: (checkins || []) as unknown as CheckinSummary[],
        usersToNotify: (checkins || []).length,
      });
    }
  }

  return asResponse<UnreviewedCheckinsByTeamsResponse>({ teams: result });
};

const getEveryonePendingCheckins = async (): Promise<AxiosResponse<PendingUsersCheckins>> => {
  const { count, error } = await supabase
    .from('checkins')
    .select('*', { count: 'exact', head: true })
    .eq('is_reviewed', false);

  if (error) throw error;
  return asResponse<PendingUsersCheckins>({ totalOfPendingCheckins: count || 0 });
};

export default {
  getAllCheckinsUnreviewed,
  getAllCheckinsUnreviewedByTeamId,
  getAllCheckinsUnreviewedByTeam,
  getEveryonePendingCheckins,
};
